import { Order } from 'models/order';
import { Steps } from 'models/workflow-step';

export type OrderNextStep = {
    order: Order;
    nextStep: Steps;
};

type ProductionPlanStep = NonNullable<Order['productionPlan']>[number];

/**
 * The next step in the order's own production plan: the plan entry after the
 * order's current step, or the first plan entry when the order has no step yet.
 * Null when production planning is off, the order has no production plan, its
 * current step is not part of the plan, the plan is exhausted, or all steps
 * are already done.
 */
export const getNextProductionPlanStep = (
    order: Order,
    isProductionPlanningEnabled: boolean,
): ProductionPlanStep | null => {
    if (!isProductionPlanningEnabled || !order.productionPlan?.length) {
        return null;
    }
    const plan = order.productionPlan;
    const currentStepId = order.workFlow?.currentStep?.stepId;
    if (!currentStepId) {
        if (order.workFlow?.currentStep?.name === 'ALL_STEPS_DONE') {
            return null;
        }
        return plan[0];
    }
    const index = plan.findIndex((planStep) => planStep.stepId === currentStepId);
    if (index === -1 || index >= plan.length - 1) {
        return null;
    }
    return plan[index + 1];
};

/**
 * The workflow step definition (from the lab's workflow) for the order's next
 * production plan step. Undefined when the order has no natural next step.
 */
export const getNextWorkflowStep = (
    order: Order,
    workflowSteps: Steps[],
    isProductionPlanningEnabled: boolean,
): Steps | undefined => {
    const nextPlanStep = getNextProductionPlanStep(order, isProductionPlanningEnabled);
    if (!nextPlanStep) {
        return undefined;
    }
    return workflowSteps.find((step) => step._id === nextPlanStep.stepId);
};

/**
 * Splits orders into those that can be moved to their natural next step and
 * those that have no next step.
 */
export const splitOrdersByNextStep = (
    orders: Order[],
    workflowSteps: Steps[],
    isProductionPlanningEnabled: boolean,
): { movable: OrderNextStep[]; unmovable: Order[] } => {
    const movable: OrderNextStep[] = [];
    const unmovable: Order[] = [];
    orders.forEach((order) => {
        const nextStep = getNextWorkflowStep(order, workflowSteps, isProductionPlanningEnabled);
        if (nextStep) {
            movable.push({ order, nextStep });
        } else {
            unmovable.push(order);
        }
    });
    return { movable, unmovable };
};
